import { EmptyListContent } from '@euler/app/components/list/EmptyListContent';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Img } from '@euler/components/image/AdvancedImage';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { isAlbumByYearMonth, MediaAlbum } from '@euler/model';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { ImageStyle } from 'react-native-fast-image';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kMargin = 5;
const kMaxWidth = 220;

const keyExtractor = (album: MediaAlbum) =>
  album.label ?? `${JSON.stringify(album.value)}`;

function defaultFormatter(album: MediaAlbum) {
  if (isAlbumByYearMonth(album)) {
    return `${album.value.year}年${album.value.month}月`;
  }
  return `${album.value}公里`;
}

export const MediaAlbumListView = memo(
  ({
    albums,
    loading,
    refreshing,
    error,
    contentContainerStyle,
    formatTitle = defaultFormatter,
    onAlbumPress,
    onRefresh,
    ...props
  }: {
    albums: MediaAlbum[];
    loading?: boolean;
    refreshing?: boolean;
    error?: Error;
    formatTitle?: (album: MediaAlbum) => string;
    onAlbumPress?: (album: MediaAlbum) => void;
    onRefresh?: () => void;
  } & Omit<ScrollViewProps, 'refreshControl'>) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const width = useContainerWidth();
    const isMobile = useIsMobileLayout();
    const numberOfColumns = isMobile ? 2 : Math.floor(width / rpx(kMaxWidth));
    const cellMargin = rpx(kMargin);
    const [cellWidth, cellHeight] = computeGridLayoutItemSize({
      gridWidth: width,
      numberOfColumns,
      aspectRatio: 5 / 4,
      gridPadding: rpx(10),
      cellMargin,
    });
    const styles = useCellStyles({ cellWidth, cellHeight, cellMargin });
    const handleAlbumPress = usePersistFn((album: MediaAlbum) => {
      onAlbumPress?.(album);
    });
    const renderItem: ListRenderItem<MediaAlbum> = useCallback(
      ({ item }) => {
        return (
          <Cell
            album={item}
            style={styles.cell}
            imageStyle={styles.image}
            formatTitle={formatTitle}
            onPress={handleAlbumPress}
          />
        );
      },
      [styles.cell, styles.image, formatTitle, handleAlbumPress],
    );
    return (
      <FlatList
        key={numberOfColumns}
        numColumns={numberOfColumns}
        data={albums}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListEmptyComponent={
          <EmptyListContent
            loading={albums.length === 0 && loading}
            error={error}
            onRetry={onRefresh}
          />
        }
        refreshing={refreshing}
        refreshControl={
          Platform.OS !== 'web' ? (
            <RefreshControl refreshing={!!refreshing} />
          ) : undefined
        }
        contentContainerStyle={[
          {
            backgroundColor: '#fff',
            paddingVertical: rpx(10),
            paddingHorizontal: rpx(10),
            paddingBottom: rpx(15) + bottom,
            flexGrow: 1,
          },
          contentContainerStyle,
        ]}
        {...props}
      />
    );
  },
);

const Cell = memo(
  ({
    album,
    style,
    formatTitle,
    imageStyle,
    imageContainerStyle,
    onPress,
  }: {
    album: MediaAlbum;
    formatTitle: (album: MediaAlbum) => string;
    imageStyle?: StyleProp<ImageStyle>;
    imageContainerStyle?: StyleProp<ImageStyle>;
    style?: StyleProp<ViewStyle>;
    onPress?: (album: MediaAlbum) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(() => {
      onPress?.(album);
    }, [album, onPress]);

    return (
      <TouchableOpacity style={style} onPress={handlePress}>
        <View
          style={imageContainerStyle}
          css={`
            border-radius: ${rpx(15)}px;
            overflow: hidden;
            margin-bottom: ${rpx(3)}px;
          `}
        >
          <Img uri={album.coverUrl} style={imageStyle} />
          <View
            style={StyleSheet.absoluteFill}
            css={`
              background-color: rgba(0, 0, 0, 0.25);
            `}
          />
          <Center
            css={`
              position: absolute;
              background-color: #fff;
              height: ${rpx(22)}px;
              min-width: ${rpx(22)}px;
              padding: 0 ${rpx(6)}px;
              border-radius: ${rpx(22)}px;
              right: ${rpx(8)}px;
              bottom: ${rpx(8)}px;
            `}
          >
            <Text
              css={`
                font-size: ${rpx(13)}px;
                font-weight: 600;
                color: #333;
              `}
            >
              {album.mediaCount}
            </Text>
          </Center>
        </View>
        <Bar margin={10} />
        <Bar margin={25} />
        <Bar margin={35} />
        <Center>
          <View>
            <Typography.Label medium size={rpx(18)} color="#333" align="center">
              {formatTitle(album)}
            </Typography.Label>
            {album.active ? (
              <View
                css={`
                  background-color: ${StatusColors.Success};
                  width: ${rpx(6)}px;
                  height: ${rpx(6)}px;
                  border-radius: ${rpx(8)}px;
                  position: absolute;
                  right: ${rpx(-10)}px;
                  top: ${rpx(4)}px;
                `}
              />
            ) : null}
          </View>
        </Center>
      </TouchableOpacity>
    );
  },
);

const Bar = memo(({ margin }: { margin: number }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        background-color: #eee;
        height: ${rpx(4)}px;
        border-bottom-left-radius: ${rpx(15)}px;
        border-bottom-right-radius: ${rpx(15)}px;
        margin: ${rpx(3)}px ${rpx(margin)}px;
      `}
    />
  );
});

const useCellStyles = (args: {
  cellWidth: number;
  cellHeight: number;
  cellMargin: number;
}) => {
  const { cellWidth, cellHeight, cellMargin } = args;
  return {
    cell: {
      width: cellWidth,
      margin: cellMargin,
      marginBottom: cellMargin * 2,
    },
    image: {
      width: cellWidth,
      height: cellHeight,
    },
  };
};
